import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { isMobileWidth } from '../../base/package/util/device';
import styles from './ui-btn.css?inline';
import '../ui-spinner/ui-spinner';

const CName = 'ui-btn';

type ButtonVariant =
  | 'accent'
  // light in a dark theme / dark in a light theme
  | 'secondary'
  | 'secondary-outlined'
  // stays light in dark and light themes
  | 'light'
  | 'light-outlined'
  // stays dark in dark and light themes
  | 'dark'
  // looks like a regular link
  | 'link'
  // transparent, no additional styles
  | 'text';

type ButtonSize = 'default' | 'text-like' | 'custom';

/**
 * @prop {ButtonVariant} variant
 * @prop {ButtonSize} size
 */
@customElement(CName)
export class UIBtn extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: String }) variant: ButtonVariant = 'secondary';
  @property({ type: String }) size: ButtonSize = 'default';
  @property({ type: String }) href = '';
  @property({ type: Boolean }) isBlock = false;
  @property({ type: Boolean }) isSeoLink = false;
  @property({ type: Boolean }) isDisabled = false;
  @property({ type: Boolean }) isLoading = false;

  render() {
    const btnClasses = classMap({
      'ui-button': true,
      [`ui-button--${this.variant}`]: this.variant,
      [`ui-button--${this.size}-size`]: this.size,
      'ui-button--disabled': this.isDisabled,
      'ui-button--loading': this.isLoading,
      'ui-button--block': this.isBlock,
      'ui-button--mobile': isMobileWidth(),
    });

    if (this.href) {
      return html`
        <a
          class=${btnClasses}
          .href=${this.href}
          @click=${this._handleLinkClick}
        >
          ${this._renderButtonContent()}
        </a>
      `;
    }

    return html`
      <button
        class=${btnClasses}
        ?disabled=${this.isDisabled}
        @click=${this._handleClick}
      >
        ${this._renderButtonContent()}
      </button>
    `;
  }

  private _renderButtonContent() {
    return html`
      ${this.isLoading ? html`<ui-spinner class="ui-button__loader"></ui-spinner><span class="visually-hidden"><slot></slot></span>` : html`<slot></slot>`}
    `;
  }

  private _handleLinkClick(ev: PointerEvent) {
    if (this.isSeoLink) {
      ev.preventDefault();
      this._handleClick(ev);
    }
  }

  private _handleClick(ev: PointerEvent) {
    if (this.isLoading || this.isDisabled) {
      ev.stopPropagation();
      ev.preventDefault();
      return;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIBtn;
  }
}
